import React, { useEffect, useState } from 'react'
import DraggableItem from 'Components/Studio/Canvas/Items/_DraggableItem'
import FontFamilyDropdown from 'Components/Studio/SideMenu/TextBlock/FontFamilyDropdown'
import useFontLoader from 'Hooks/useFontLoader'
import { Font, FontVariant } from 'Vendor/Utils/StudioTypes'
import { FONT_WEIGHT } from 'Vendor/Utils/Constants'

const TYPOGRAPHY_STYLES = {
    title1: { fontSize: '32px' },
    title2: { fontSize: '24px' },
    title3: { fontSize: '20px' },
}

interface TextBlockProps {
    platformFonts?: Font[];
}

const TextBlock: React.FC<TextBlockProps> = ({ platformFonts }) => {
    const [selectedFont, setSelectedFont] = useState<string>('')
    const [selectedVariant, setSelectedVariant] = useState<FontVariant | null>(null)

    const getDefaultVariant = (font: Font): FontVariant | null => {
        if (!font.variants || font.variants.length === 0) {
            return null;
        }

        const normalizedName = (name?: string) => String(name || '').trim().toLowerCase();

        const regularVariant = font.variants.find(
            (variant) => normalizedName(variant.name) === 'regular'
        );

        if (regularVariant) {
            return regularVariant;
        }

        const normalVariant = font.variants.find(
            (variant) =>
                normalizedName(variant.style) === 'normal' ||
                variant.weight === FONT_WEIGHT.REGULAR
        );

        return normalVariant || font.variants[0];
    };

    useEffect(() => {
        if (!selectedFont || selectedVariant || !platformFonts || platformFonts.length === 0) {
            return;
        }

        const font = platformFonts.find((f) => f.family === selectedFont);
        const defaultVariant = font ? getDefaultVariant(font) : null;

        if (defaultVariant) {
            setSelectedVariant(defaultVariant);
        }
    }, [selectedFont, selectedVariant, platformFonts]);

    useFontLoader({
        family: selectedFont,
        weight: selectedVariant?.weight,
        style: selectedVariant?.style,
        platformFonts,
    });

    const handleVariantChange = (_family: string, variant: FontVariant) => {
        setSelectedVariant(variant);
    };

    return (
        <>
            <div className="my-3">
                <FontFamilyDropdown
                    label="Typography"
                    onChange={setSelectedFont}
                    onVariantChange={handleVariantChange}
                    value={selectedFont}
                    platformFonts={platformFonts}
                    showVariants
                    selectedVariantName={selectedVariant?.name}
                />
            </div>

            <div className="mb-3" style={{ zIndex: 0 }}>
                {Object.entries(TYPOGRAPHY_STYLES).map(([key, style]) => (
                    <DraggableItem
                        key={key}
                        id={key}
                        label="Title"
                        type="TEXT"
                        style={{
                            ...style,
                            fontFamily: selectedFont,
                            fontWeight: selectedVariant?.weight || String(FONT_WEIGHT.REGULAR),
                            fontStyle: selectedVariant?.style || 'normal',
                        }}
                    />
                ))}
            </div>
        </>
    )
}

export default TextBlock
